'use client';

import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { LOCATION } from 'src/hooks/filters/constants';
import {
  CriteriaComponentType,
  replaceDynamicCriteria,
  clearDynamicCriteria as clearDynamicCriteriaAction,
} from 'src/redux/slices/aiListGeneratorSlice';
import {
  updateFreshFacesData,
  updateNotReferralsData,
  updatePatientJourneyData,
  TemplateConfig,
} from 'src/redux/slices/strategyFormSlice';
import { transformTemplateWithStaticTypes } from 'src/components/DynamicCriteriaGenerator/utils/placeholderUtils';
import { CriteriaInput } from 'src/components/DynamicCriteriaGenerator/types/criteriaTemplate';
import { ALL_CRITERIA_TYPES } from 'src/components/DynamicCriteriaGenerator/constants';

/**
 * Extracts allowed CriteriaComponentTypes from a template's criteria_inputs.
 * Only returns types that exist in ALL_CRITERIA_TYPES.
 */
export const getAllowedTypesFromConfig = (
  criteriaInputs?: Record<string, { type: string }>
): CriteriaComponentType[] => {
  if (!criteriaInputs) {
    return [];
  }

  const types = new Set<CriteriaComponentType>();
  const validTypes = new Set(ALL_CRITERIA_TYPES);

  Object.values(criteriaInputs).forEach((input) => {
    if (validTypes.has(input.type as CriteriaComponentType)) {
      types.add(input.type as CriteriaComponentType);
    }
  });

  return Array.from(types);
};

/**
 * Hook for hydrating template data into Redux state.
 * Consolidates the template hydration logic used by TargetStrategies,
 * EngageStrategies, and TemplateDrawer.
 *
 * @param location - The Redux location key for storing dynamic criteria
 */
export const useTemplateHydration = (location: string) => {
  const dispatch = useDispatch();

  /**
   * Hydrates a template configuration into Redux state.
   * Handles type-specific logic for FRESH_FACES, PATIENT_JOURNEY, NOT_REFERRING,
   * and regular template types (SCOUT_LIST, REFERRALS, TREATING_VOLUME).
   */
  const hydrateTemplate = useCallback(
    (config: TemplateConfig, strategyType: string) => {
      const isFreshFaces = strategyType === 'FRESH_FACES';
      const isPatientJourney = strategyType === 'PATIENT_JOURNEY';

      if (isFreshFaces) {
        // FRESH_FACES: Hydrate fresh faces data, clear dynamic criteria
        dispatch(clearDynamicCriteriaAction({ location }));

        const freshFacesUpdates: Record<string, unknown> = {};
        if (config.code_set !== undefined) {
          freshFacesUpdates.code_set = config.code_set;
        }
        if (config.filter_set !== undefined) {
          freshFacesUpdates.filter_set = config.filter_set;
        }
        if (config.volume_type !== undefined) {
          freshFacesUpdates.volume_type = config.volume_type;
        }
        if (config.provider_type !== undefined) {
          freshFacesUpdates.provider_type = config.provider_type;
        }
        if (Object.keys(freshFacesUpdates).length > 0) {
          dispatch(updateFreshFacesData(freshFacesUpdates));
        }
      } else if (isPatientJourney) {
        // PATIENT_JOURNEY: Handle index and target event templates
        // Populate index event editor
        if (config.index_event_template) {
          const { html: indexHtml, criteriaInputs: indexInputs } =
            transformTemplateWithStaticTypes(
              config.index_event_template.criteria_template,
              config.index_event_template.criteria_inputs as Record<
                string,
                CriteriaInput
              >
            );
          dispatch(
            replaceDynamicCriteria({
              criteria: {
                criteria_template: indexHtml,
                criteria_inputs: indexInputs,
              },
              location: LOCATION.PATIENT_JOURNEY_INDEX_EVENT,
            })
          );
        }

        // Populate target event editor
        if (config.target_event_template) {
          const { html: targetHtml, criteriaInputs: targetInputs } =
            transformTemplateWithStaticTypes(
              config.target_event_template.criteria_template,
              config.target_event_template.criteria_inputs as Record<
                string,
                CriteriaInput
              >
            );
          dispatch(
            replaceDynamicCriteria({
              criteria: {
                criteria_template: targetHtml,
                criteria_inputs: targetInputs,
              },
              location: LOCATION.PATIENT_JOURNEY_TARGET_EVENT,
            })
          );
        }

        // Hydrate PATIENT_JOURNEY specific fields
        const patientJourneyUpdates: Record<string, unknown> = {};
        if (config.index_event_radius !== undefined) {
          patientJourneyUpdates.radius = config.index_event_radius;
        }
        if (config.target_event_interval !== undefined) {
          patientJourneyUpdates.target_event_interval =
            config.target_event_interval;
        }
        if (config.target_event_direction !== undefined) {
          patientJourneyUpdates.target_event_direction =
            config.target_event_direction;
        }
        if (Object.keys(patientJourneyUpdates).length > 0) {
          dispatch(updatePatientJourneyData(patientJourneyUpdates));
        }
      } else {
        // Regular template: SCOUT_LIST, REFERRALS, NOT_REFERRING, TREATING_VOLUME
        const { html, criteriaInputs } = transformTemplateWithStaticTypes(
          config.criteria_template || '',
          (config.criteria_inputs || {}) as Record<string, CriteriaInput>
        );

        // Use atomic replace action - no need for clear + setTimeout + set
        dispatch(
          replaceDynamicCriteria({
            criteria: {
              criteria_template: html,
              criteria_inputs: criteriaInputs,
            },
            location,
          })
        );

        // Hydrate NOT_REFERRING specific fields
        if (strategyType === 'NOT_REFERRING' && config.radius !== undefined) {
          dispatch(updateNotReferralsData({ radius: config.radius }));
        }
      }
    },
    [dispatch, location]
  );

  return { hydrateTemplate };
};

export default useTemplateHydration;
